import { useEffect, useState } from 'react';
import { listDict, listDictCategories, loadProcessArchsCached } from '../api/dict';
import { useSchemaFieldEnumSources } from './useSchemaFieldNames';

// 字典来源枚举的公共取数层 (设备 Schema 表单 / 参数动态 Schema 表单共用).
// 枚举来源由 SSOT schema_field.enums_source 决定, 读路径富化成字段上的 optionsSource / os,
// 前端按类目拉一次字典拿 code→label. 类目是小静态数据, 这里做模块级缓存, 全站每 (类目, scope) 一次请求.
//
// scoped 类目 (如 process_type 按工艺框架分列表) 必须带 scope 才拿得到条目,
// 所以先读一次 dict-categories 拿 scoped 标记, 再决定这次请求带不带 scope.

export type DictOptions = Record<string, { code: string; label: string }[]>;

const cache = new Map<string, { code: string; label: string }[]>();
const inflight = new Map<string, Promise<{ code: string; label: string }[]>>();

let scopedCats: Promise<Set<string>> | null = null;
function scopedCategories(): Promise<Set<string>> {
    if (!scopedCats) {
        scopedCats = listDictCategories()
            .then((cs) => new Set(cs.filter((c) => c.scoped).map((c) => c.code)))
            .catch(() => {
                scopedCats = null; // 失败不缓存
                return new Set<string>();
            });
    }
    return scopedCats;
}

function loadCategory(cat: string, scope: string): Promise<{ code: string; label: string }[]> {
    const key = `${cat}|${scope}`;
    const hit = cache.get(key);
    if (hit) return Promise.resolve(hit);
    let p = inflight.get(key);
    if (!p) {
        p = scopedCategories()
            .then((scoped) => listDict(cat, scoped.has(cat) && scope ? { scope } : {}))
            .then((es) => {
                const opts = es.map((e) => ({ code: e.code, label: e.label }));
                cache.set(key, opts);
                return opts;
            })
            .catch((e) => {
                inflight.delete(key); // 失败不缓存, 下次重试
                throw e;
            });
        inflight.set(key, p);
    }
    return p;
}

/**
 * 拉一批枚举来源字典类目的选项 (失败静默, 渐进填充).
 * scope: scoped 类目的上下文 (参数管理传 archCode); 非 scoped 类目忽略此值.
 */
export function useDictOptions(categories: (string | undefined)[], scope = ''): DictOptions {
    const [opts, setOpts] = useState<DictOptions>({});
    const key = Array.from(new Set(categories.filter(Boolean) as string[]))
        .sort()
        .join(',');
    useEffect(() => {
        if (!key) return;
        key.split(',').forEach((cat) => {
            loadCategory(cat, scope)
                .then((list) => setOpts((prev) => (prev[cat] === list ? prev : { ...prev, [cat]: list })))
                .catch(() => {});
        });
    }, [key, scope]);
    return opts;
}

/**
 * scoped 类目的 code → label 取值器 (如 process_type 按工艺框架分 scope).
 * scopes 传上下文出现过的全部 scope (列表页多框架混排时逐个懒加载);
 * 取值优先命中该行自己的 scope, 未命中时兜底查其余已加载 scope
 * (同 code 跨框架同义, 如 VECTOR_CUTTING 在 frame/galvo 都叫矢量切割),
 * 全部落空回退原 code, 不显示空白.
 */
export function useScopedDictText(
    category: string,
    scopes: (string | undefined)[],
): (scope: string | undefined, code?: string, fallback?: string) => string {
    const [maps, setMaps] = useState<Record<string, Record<string, string>>>({});
    const key = Array.from(new Set(scopes.filter(Boolean) as string[]))
        .sort()
        .join(',');
    useEffect(() => {
        if (!key) return;
        key.split(',').forEach((scope) => {
            loadCategory(category, scope)
                .then((list) =>
                    setMaps((prev) =>
                        prev[scope] ? prev : { ...prev, [scope]: Object.fromEntries(list.map((o) => [o.code, o.label])) },
                    ),
                )
                .catch(() => {});
        });
    }, [category, key]);
    return (scope, code, fallback = '—') => {
        if (!code) return fallback;
        const own = scope ? maps[scope]?.[code] : undefined;
        if (own) return own;
        for (const m of Object.values(maps)) {
            if (m[code]) return m[code];
        }
        return code;
    };
}

/** 枚举值 → 显示文案: 字典命中用 label, 未命中回退原值 (不吞数据). */
export function dictOptionLabel(opts: DictOptions, source: string | undefined, code: string): string {
    if (!source || !code) return code;
    return opts[source]?.find((o) => o.code === code)?.label || code;
}

/**
 * 非 scoped 类目的 code → 显示文案取值器 (如 light_source / bitmap_mode). 未命中回退原 code.
 * 大小写不敏感兜底: 采集/上报数据的枚举拼写与字典常有大小写差异 (如 Grayscale vs grayscale),
 * 精确命中优先, 再按小写比一次, 都落空才回退原值.
 */
export function useDictText(category: string): (code?: string, fallback?: string) => string {
    const opts = useDictOptions([category]);
    return (code, fallback = '—') => {
        if (!code) return fallback;
        return labelOf(opts[category], code);
    };
}

/**
 * process_type 是按工艺框架 scope 分列表的类目, 但展示侧 (数据资产等) 拿到的记录不带框架,
 * 所以这里把全部启用框架的条目合并成一张 code → label 表 (同 code 跨框架同义, 保留首个).
 * 用它替代页面里手抄的工艺类型常量表.
 */
function useAllArchProcessTypes(): { code: string; label: string }[] {
    const [list, setList] = useState<{ code: string; label: string }[]>([]);
    useEffect(() => {
        let alive = true;
        loadProcessArchsCached()
            .then((archs) => Promise.all(archs.map((a) => loadCategory('process_type', a.code).catch(() => []))))
            .then((groups) => {
                if (!alive) return;
                const seen = new Map<string, string>();
                groups.flat().forEach((o) => {
                    if (!seen.has(o.code)) seen.set(o.code, o.label);
                });
                setList(Array.from(seen, ([code, label]) => ({ code, label })));
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);
    return list;
}

/** 工艺类型 code → 显示文案 (跨全部框架合并; 未命中回退原 code). */
export function useProcessTypeText(): (code?: string, fallback?: string) => string {
    const list = useAllArchProcessTypes();
    return (code, fallback = '—') => {
        if (!code) return fallback;
        return list.find((o) => o.code === code)?.label || code;
    };
}

/** 工艺类型筛选下拉的选项 (跨全部框架合并). */
export function useProcessTypeOptions(): { code: string; label: string }[] {
    return useAllArchProcessTypes();
}

/**
 * 一份字典选项里按 code 取 label: 精确命中优先, 再按小写比一次 (上报数据与字典常有大小写差异),
 * 都落空回退原 code —— 不吞数据.
 */
function labelOf(list: { code: string; label: string }[] | undefined, code: string): string {
    if (!list) return code;
    return (list.find((o) => o.code === code) || list.find((o) => o.code.toLowerCase() === code.toLowerCase()))?.label || code;
}

/**
 * 参数字段值 → 中文, 绑定关系取自 schema_field.enums_source (SSOT: 字段字典说哪个字段用哪本字典),
 * 所以页面不需要自己维护"字段 → 字典类目"的对照表, 后台把某字段挂上枚举来源即自动生效.
 * 字段没挂枚举来源, 或字典里没这个 code, 一律回退原值.
 */
export function useEnumSourceText(): (fieldKey: string, code: string) => string {
    const sources = useSchemaFieldEnumSources();
    const cats = Array.from(new Set(sources.values()));
    const opts = useDictOptions(cats);
    return (fieldKey, code) => {
        const cat = sources.get(fieldKey);
        if (!cat || !code) return code;
        return labelOf(opts[cat], code);
    };
}
